import { useState } from 'react'
import { Archive, ArrowLeft, Loader2, Pause, Pencil, Play } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { LoadError } from '@/components/campaigns/LoadError'
import { RecipientsTable } from '@/components/campaigns/RecipientsTable'
import { CampaignStatusChip } from '@/components/campaigns/StatusChip'
import { formatNumber } from '@/components/campaigns/campaignFormat'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PageHeader } from '@/components/ui/page-header'
import { PageToolbar } from '@/components/ui/page-toolbar'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useCampaignFilterOptionsQuery,
  useCampaignStatsQuery,
  useCampaignTransition,
  useCrmCampaignQuery,
  type CampaignTransition,
} from '@/queries'
import type { CampaignStats, CrmCampaign, RecipientStatus } from '@/services/crmCampaignsService'

const ALL = '__all'

const STAT_KEYS: Array<keyof CampaignStats> = [
  'destinatarios',
  'enviados',
  'entregados',
  'leidos',
  'respondidos',
  'visitas',
  'bajas',
  'bajas_por_revisar',
  'fallidos',
  'parados_gestion',
  'descartados_consentimiento',
]

const RECIPIENT_STATUSES: RecipientStatus[] = [
  'pendiente',
  'en_curso',
  'respondido',
  'completado',
  'baja',
  'parado_gestion_agente',
  'parado_demanda_baja',
  'descartado_rgpd',
  'fallido',
  'excluido',
]

function CampaignPanel({ campaign }: { campaign: CrmCampaign }) {
  const { t } = useTranslation()
  const { lang = 'es' } = useParams()
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [agent, setAgent] = useState(ALL)
  const [recipientStatus, setRecipientStatus] = useState(ALL)
  const agentParam = agent === ALL ? undefined : agent
  const options = useCampaignFilterOptionsQuery()
  const stats = useCampaignStatsQuery(
    campaign.id,
    { desde: from || undefined, hasta: to || undefined, gestor: agentParam },
    campaign.estado === 'activa',
  )

  return (
    <div className="space-y-8">
      <PageToolbar className="items-end">
        <div className="space-y-1.5">
          <Label htmlFor="stats-from">{t('campaigns.detail.from')}</Label>
          <Input id="stats-from" aria-label={t('campaigns.detail.from')} className="w-44" type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="stats-to">{t('campaigns.detail.to')}</Label>
          <Input id="stats-to" aria-label={t('campaigns.detail.to')} className="w-44" type="date" value={to} onChange={(event) => setTo(event.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="stats-agent">{t('campaigns.detail.agent')}</Label>
          <Select value={agent} onValueChange={setAgent}>
            <SelectTrigger id="stats-agent" className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('campaigns.detail.all_agents')}</SelectItem>
              {(options.data?.gestores ?? []).map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </PageToolbar>

      {stats.isPending ? (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5" data-testid="stats-loading">
          {STAT_KEYS.map((key) => (
            <Skeleton key={key} className="h-16 w-full" />
          ))}
        </div>
      ) : stats.isError ? (
        <LoadError error={stats.error} onRetry={() => void stats.refetch()} />
      ) : (
        <dl className="grid grid-cols-2 gap-4 md:grid-cols-5">
          {STAT_KEYS.map((key) => (
            <div key={key} className="space-y-1" title={t(`campaigns.detail.stat_help.${key}`)}>
              <dd className="text-2xl font-semibold tabular-nums" data-testid={`stat-${key}`}>
                {formatNumber(stats.data[key], lang)}
              </dd>
              <dt className="text-xs text-muted-foreground">{t(`campaigns.detail.stat.${key}`)}</dt>
            </div>
          ))}
        </dl>
      )}

      <section className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <h2 className="text-lg font-semibold">{t('campaigns.detail.recipients_title')}</h2>
          <div className="space-y-1.5">
            <Label htmlFor="recipient-status">{t('campaigns.detail.recipient_status')}</Label>
            <Select value={recipientStatus} onValueChange={setRecipientStatus}>
              <SelectTrigger id="recipient-status" className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t('campaigns.detail.all_statuses')}</SelectItem>
                {RECIPIENT_STATUSES.map((status) => (
                  <SelectItem key={status} value={status}>
                    {t(`campaigns.recipient_status.${status}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <RecipientsTable
          campaignId={campaign.id}
          mode="monitor"
          status={recipientStatus === ALL ? undefined : recipientStatus}
          agent={agentParam}
        />
      </section>
    </div>
  )
}

function CampaignActions({ campaign }: { campaign: CrmCampaign }) {
  const { t } = useTranslation()
  const { lang = 'es' } = useParams()
  const transition = useCampaignTransition(campaign.id)
  const [pending, setPending] = useState<CampaignTransition | null>(null)

  const confirm = () => {
    if (!pending) return
    transition.mutate(pending, {
      onSuccess: () => {
        toast.success(t(`campaigns.detail.action.${pending}.done`))
        setPending(null)
      },
      onError: (error) => {
        toast.error(error.message)
        setPending(null)
      },
    })
  }

  return (
    <>
      {campaign.estado === 'borrador' ? (
        <Button asChild variant="outline">
          <Link to={`/${lang}/campaigns/${campaign.id}/edit`}>
            <Pencil className="mr-2 h-4 w-4" />
            {t('campaigns.detail.continue_editing')}
          </Link>
        </Button>
      ) : null}
      {campaign.estado === 'activa' ? (
        <Button variant="outline" onClick={() => setPending('pause')}>
          <Pause className="mr-2 h-4 w-4" />
          {t('campaigns.detail.action.pause.button')}
        </Button>
      ) : null}
      {campaign.estado === 'pausada' ? (
        <Button onClick={() => setPending('resume')}>
          <Play className="mr-2 h-4 w-4" />
          {t('campaigns.detail.action.resume.button')}
        </Button>
      ) : null}
      {campaign.estado !== 'archivada' ? (
        <Button variant="ghost" onClick={() => setPending('archive')}>
          <Archive className="mr-2 h-4 w-4" />
          {t('campaigns.detail.action.archive.button')}
        </Button>
      ) : null}

      <AlertDialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
        <AlertDialogContent>
          {pending ? (
            <AlertDialogHeader>
              <AlertDialogTitle>{t(`campaigns.detail.action.${pending}.title`)}</AlertDialogTitle>
              <AlertDialogDescription>{t(`campaigns.detail.action.${pending}.description`)}</AlertDialogDescription>
            </AlertDialogHeader>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel>{t('campaigns.common.cancel')}</AlertDialogCancel>
            <AlertDialogAction disabled={transition.isPending} onClick={confirm}>
              {transition.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {pending ? t(`campaigns.detail.action.${pending}.button`) : null}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export function CampaignDetailPage() {
  const { t } = useTranslation()
  const { id = '', lang = 'es' } = useParams()
  const campaign = useCrmCampaignQuery(id)

  return (
    <div className="container mx-auto space-y-8 p-4 md:p-6">
      <Button asChild variant="link">
        <Link to={`/${lang}/campaigns`}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('campaigns.common.back_to_list')}
        </Link>
      </Button>

      {campaign.isPending ? (
        <div className="space-y-4" data-testid="detail-loading">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : campaign.isError ? (
        <LoadError error={campaign.error} onRetry={() => void campaign.refetch()} />
      ) : (
        <>
          <PageHeader
            title={
              <span className="flex flex-wrap items-center gap-2">
                {campaign.data.nombre}
                <CampaignStatusChip status={campaign.data.estado} />
              </span>
            }
          >
            <CampaignActions campaign={campaign.data} />
          </PageHeader>
          {campaign.data.estado === 'borrador' ? (
            <EmptyState
              title={t('campaigns.detail.draft_title')}
              description={t('campaigns.detail.draft_description')}
            />
          ) : (
            <CampaignPanel campaign={campaign.data} />
          )}
        </>
      )}
    </div>
  )
}
